/* components/patterns/States.jsx — единые состояния: Skeleton, EmptyState,
   ErrorState, ForbiddenState, NotFoundState, LoadingState. Каждый экран обязан их иметь. */

import React from 'react';
import { Button } from '../ui/Button.jsx';
import { Icon } from '../ui/Icon.jsx';

  const { useEffect, useRef } = React;

  // role=status: загрузку объявляет и скринридер, а не только крутилка
  function LoadingState({ label = 'Загрузка…' }) {
    return (
      <div className="state state--loading" role="status">
        <Icon name="spinner" size={28} />
        <div className="t-body text-dim">{label}</div>
      </div>
    );
  }

  /* EmptyState (kitchen-ux K-50; канон 01 §2.4 «засечки — только обложки», §8.1).
     serif — ТОЛЬКО для обложек (приветственный / первый экран раздела): по умолчанию
     заголовок без засечек. size="inline" — пустая секция внутри редактора или карточки:
     компактный блок, заголовок — абзац (секция уже озаглавлена), засечек нет никогда. */
  function EmptyState({ icon = 'grid', title, body, action, serif = false, size = 'block', className }) {
    const inline = size === 'inline';
    return (
      <div className={'state state--empty' + (inline ? ' state--inline' : '') + (className ? ' ' + className : '')}>
        <div className="state__glyph"><Icon name={icon} size={inline ? 18 : 26} /></div>
        {inline
          ? <p className="state__title state__title--inline">{title}</p>
          : <h3 className={serif ? 't-display state__title-serif' : 't-h3'}>{title}</h3>}
        {body && <p className={'state__body ' + (inline ? 't-body-sm' : 't-body') + ' text-dim'}>{body}</p>}
        {action && <div className="state__action">{action}</div>}
      </div>
    );
  }

  function ErrorState({ title = 'Что-то пошло не так', body = 'Не удалось загрузить данные. Попробуйте ещё раз.', onRetry }) {
    return (
      <div className="state state--error">
        <div className="state__glyph state__glyph--bad"><Icon name="alert-triangle" size={26} /></div>
        <h3 className="t-h3">{title}</h3>
        <p className="state__body t-body text-dim">{body}</p>
        {onRetry && <Button variant="secondary" iconLeft="refresh" onClick={onRetry}>Повторить</Button>}
      </div>
    );
  }

  function ForbiddenState({ title = 'Нет доступа', body = 'У вас нет прав для просмотра этого раздела. Обратитесь к администратору пространства.' }) {
    return (
      <div className="state state--forbidden">
        <div className="state__glyph state__glyph--warn"><Icon name="alert-circle" size={26} /></div>
        <h3 className="t-h3">{title}</h3>
        <p className="state__body t-body text-dim">{body}</p>
      </div>
    );
  }

  /* «Не найдено» (404, kitchen-ux G4 П3): сервер отвечает 404 и на удалённое, и на чужое
     пространство (изоляция, S10) — поэтому «удалили или нет доступа», а не «Что-то пошло не так …
     Повторить»: повтор тут ничего не даст. Разметка — как у EditorNotFound редакторов
     «Продукции» (C-39): классы .state, глиф поиска, заголовок, текст, необязательное действие
     («К списку»). По умолчанию фокусом не управляет (заголовок h3): состояние живёт и внутри
     разделов настроек и списков. focusHeading — состояние заменяет ВСЁ содержимое окна
     (карточка позиции кассы в drawer-стеке): как в EditorNotFound, заголовок — главный в окне
     (h2, tabIndex −1) и получает фокус после отрисовки — читалка произносит его (K-15, круг 2
     G4); setTimeout 0 — после начального фокуса окна, без прокрутки. */
  function NotFoundState({ title = 'Не найдено', body = 'Возможно, это удалили или у вас нет доступа.', action, focusHeading = false }) {
    const headRef = useRef(null);
    useEffect(() => {
      if (!focusHeading) return undefined;
      const t = setTimeout(() => {
        const el = headRef.current;
        if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
      }, 0);
      return () => clearTimeout(t);
    }, [focusHeading]);
    return (
      <div className="state state--empty state--not-found">
        <div className="state__glyph"><Icon name="search" size={26} /></div>
        {focusHeading
          ? <h2 className="t-h3" tabIndex={-1} ref={headRef}>{title}</h2>
          : <h3 className="t-h3">{title}</h3>}
        {body && <p className="state__body t-body text-dim">{body}</p>}
        {action && <div className="state__action">{action}</div>}
      </div>
    );
  }

  // диспетчер по {loading,error}: оборачивает контент экрана.
  // refreshing (useFakeQuery: перезапрос ТОГО ЖЕ запроса, данные на экране) — скелетон
  // только когда данных нет: реестр после «Сохранить» не мигает, прокрутка и фокус на
  // месте (kitchen-ux A2, K-33). Без refreshing — прежнее поведение.
  // 404 — «не найдено» без «Повторить» (G4 П3): копия экрана — notFoundTitle / notFoundBody,
  // действие — notFoundAction (по образцу forbiddenTitle / forbiddenBody); без них — нейтрально.
  // notFoundFocus — состояние заменяет всё окно: фокус на заголовок (NotFoundState.focusHeading).
  function QueryBoundary({ loading, refreshing, error, empty, onRetry, children,
                          loadingEl, emptyEl, skeleton, forbiddenBody, forbiddenTitle,
                          notFoundTitle, notFoundBody, notFoundAction, notFoundFocus = false }) {
    if (loading && !refreshing) return skeleton || loadingEl || <LoadingState />;
    if (error && error.status === 403) {
      // контекстная 403-копия: явный пропс forbiddenBody либо err.detail из API
      const body = forbiddenBody || (error && (error.detail || error.message)) || undefined;
      return <ForbiddenState title={forbiddenTitle} body={body} />;
    }
    // detail 404 у DRF — «Не найдено.» / «No … matches the given query»: свою копию не подменяем
    if (error && error.status === 404) {
      return <NotFoundState title={notFoundTitle} body={notFoundBody} action={notFoundAction} focusHeading={!!notFoundFocus} />;
    }
    if (error) return <ErrorState onRetry={onRetry} />;
    if (empty) return emptyEl || <EmptyState title="Пока пусто" />;
    return children;
  }

export { LoadingState };
export { EmptyState };
export { ErrorState };
export { ForbiddenState };
export { NotFoundState };
export { QueryBoundary };
